import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listBillingReconciliationAppSheetBacAccounts,
  listBillingReconciliationAppSheetRows,
  listBillingReconciliationCpqRows,
  listBillingReconciliationDpeRows,
  listBillingReconciliationGmRows,
  listBillingReconciliationHistoricalFinalGmRows,
  searchBillingReconciliationAppSheetAccounts,
} from './billingReconciliationApi.js';
import {
  getBillingReconciliationOperatorContext,
  getBillingReconciliationContextWarmStatus,
  queueBillingReconciliationContextWarm,
  refreshBillingReconciliationBacContext,
} from './billingReconciliationBacContextApi.js';
import {
  invalidateBillingVarianceLists,
} from './billingReconciliationQueryInvalidation.js';

const rowEvidenceKey = ({ runId, bac, importMode = null }) => ['billingRecon', 'rowEvidence', runId || null, bac || null, importMode || null];
export const bacContextKey = ({ runId, bac, varianceId = null, importMode = null, contextMode = null } = {}) => ['billingRecon', 'bacContext', runId || null, bac || null, varianceId || null, importMode || null, contextMode || null];
export const operatorContextKey = ({ runId, bac, varianceId = null, importMode = null } = {}) => ['billingRecon', 'operatorContext', runId || null, bac || null, varianceId || null, importMode || null];
export const contextWarmStatusKey = ({ runId, contextMode = 'critical' } = {}) => ['billingRecon', 'contextWarmStatus', runId || null, contextMode || null];
const gmRowsKey = ({ runId, bac, limit, offset } = {}) => ['billingRecon', 'gmRows', runId || null, bac || null, limit || null, offset || null];
const appSheetAccountSearchKey = ({ runId, search, limit } = {}) => ['billingRecon', 'appsheetAccounts', runId || null, search || null, limit || null];
const appSheetBacAccountsKey = ({ runId, bac, limit } = {}) => ['billingRecon', 'appsheetBacAccounts', runId || null, bac || null, limit || null];
const noRetry = { retry: false };
const ACTIVE_WARM_STATUSES = new Set(['queued', 'warming', 'processing']);
const TERMINAL_REFRESH_STATUSES = new Set(['processed', 'failed']);

export const contextWarmStatusRefetchIntervalMs = (data) => {
  const status = String(data?.lastRun?.status || '').toLowerCase();
  const pending = Number(data?.pending || 0);
  const active = Number(data?.active || 0) + Number(data?.queued || 0) + Number(data?.processing || 0);
  const total = Number(data?.total || 0);
  const pausedWithResume = status === 'paused' && data?.lastRun?.resume?.enqueued === true;
  if (data?.statusUnavailable) return 30000;
  if (active > 0) return 5000;
  if (ACTIVE_WARM_STATUSES.has(status) || pausedWithResume) return 15000;
  if (!status && (pending > 0 || total === 0)) return 15000;
  return false;
};

const copyRowsMeta = (source, target) => {
  if (source?.meta) {
    Object.defineProperty(target, 'meta', {
      value: source.meta,
      enumerable: false,
      configurable: true,
    });
  }
  return target;
};

export const patchVarianceWarmStatuses = (queryClient, { runId, status } = {}) => {
  const warmRows = new Map((status?.bacs || [])
    .map((row) => [String(row.bac || '').trim(), {
      contextWarmStatus: row.contextWarmStatus || (row.warmedAt ? 'processed' : 'pending'),
      contextWarmedAt: row.contextWarmedAt || row.warmedAt || null,
      contextWarmError: row.contextWarmError || null,
      contextWarmAttemptCount: row.contextWarmAttemptCount || 0,
      contextWarmStartedAt: row.contextWarmStartedAt || null,
      contextWarmLastAttemptAt: row.contextWarmLastAttemptAt || null,
      contextWarmUpdatedAt: row.contextWarmUpdatedAt || null,
      contextWarmStale: Boolean(row.contextWarmStale),
    }])
    .filter(([bac]) => bac));
  const aggregateProcessed = !warmRows.size
    && Number(status?.total || 0) > 0
    && Number(status?.processed ?? status?.warmed ?? 0) >= Number(status?.total || 0)
    && Number(status?.queued || 0) === 0
    && Number(status?.processing || 0) === 0
    && Number(status?.pending || 0) === 0;
  if (!queryClient || !runId || (!warmRows.size && !aggregateProcessed)) return;
  const patchRows = (current) => {
    if (!Array.isArray(current)) return current;
    let changed = false;
    const next = current.map((row) => {
      const patch = warmRows.get(String(row?.bac || '').trim());
      if (!patch && aggregateProcessed) {
        const currentStatus = String(row?.contextWarmStatus || '').trim().toLowerCase();
        if (!['pending', 'queued', 'processing', 'invalidated', 'warming'].includes(currentStatus)) return row;
        changed = true;
        return {
          ...row,
          contextWarmStatus: 'processed',
          contextWarmedAt: status?.latestWarmedAt || row.contextWarmedAt || row.warmedAt || null,
          contextWarmError: null,
          contextWarmStale: false,
        };
      }
      if (!patch) return row;
      if (
        row.contextWarmStatus === patch.contextWarmStatus
        && row.contextWarmedAt === patch.contextWarmedAt
        && row.contextWarmError === patch.contextWarmError
        && row.contextWarmStartedAt === patch.contextWarmStartedAt
        && row.contextWarmLastAttemptAt === patch.contextWarmLastAttemptAt
        && row.contextWarmUpdatedAt === patch.contextWarmUpdatedAt
        && Boolean(row.contextWarmStale) === patch.contextWarmStale
      ) return row;
      changed = true;
      return { ...row, ...patch };
    });
    return changed ? copyRowsMeta(current, next) : current;
  };
  queryClient.setQueriesData({ queryKey: ['billingRecon', 'variancesAll', runId] }, patchRows);
  queryClient.setQueriesData({ queryKey: ['billingRecon', 'variances', runId] }, patchRows);
};

const bacWarmFingerprint = (row = {}) => [
  String(row.contextWarmStatus || (row.warmedAt ? 'processed' : 'pending')).trim().toLowerCase(),
  row.contextWarmedAt || row.warmedAt || '',
  row.contextWarmError || '',
].join(':');

const invalidateChangedTerminalBacContexts = (queryClient, {
  runId,
  rows = [],
  previousFingerprints,
} = {}) => {
  if (!queryClient || !runId || !previousFingerprints || !Array.isArray(rows)) return;
  const nextFingerprints = new Map();
  rows.forEach((row) => {
    const bac = String(row?.bac || '').trim();
    if (!bac) return;
    const status = String(row.contextWarmStatus || (row.warmedAt ? 'processed' : 'pending')).trim().toLowerCase();
    const fingerprint = bacWarmFingerprint(row);
    const previous = previousFingerprints.get(bac);
    nextFingerprints.set(bac, fingerprint);
    if (!previous || previous === fingerprint || !TERMINAL_REFRESH_STATUSES.has(status)) return;
    queryClient.invalidateQueries({
      queryKey: ['billingRecon', 'bacContext', runId, bac],
      refetchType: 'active',
    });
    queryClient.invalidateQueries({
      queryKey: ['billingRecon', 'operatorContext', runId, bac],
      refetchType: 'active',
    });
  });
  previousFingerprints.clear();
  nextFingerprints.forEach((value, key) => previousFingerprints.set(key, value));
};

const listRows = (payload) => {
  if (Array.isArray(payload)) return payload;
  const rows = payload?.rows || [];
  if (Array.isArray(rows) && payload && typeof payload === 'object') {
    Object.defineProperty(rows, 'meta', {
      value: {
        total: payload.total,
        returned: payload.returned,
        limit: payload.limit,
        offset: payload.offset,
        hasMore: payload.hasMore,
      },
      enumerable: false,
      configurable: true,
    });
  }
  return rows;
};

export const evidenceFromBacContext = (context) => ({
  gmRows: listRows(context?.evidence?.gmRows),
  cpqRows: listRows(context?.evidence?.cpqRows),
  historicalFinalGmRows: listRows(context?.evidence?.historicalFinalGmRows),
  appsheetData: context?.evidence?.appSheetRows || null,
  dpeData: context?.evidence?.dpeRows || null,
  appSheetAccounts: context?.evidence?.appSheetAccounts || { rows: [], total: 0 },
  workflow: context?.workflow || {},
  variance: context?.variance || null,
  accountMetadata: context?.accountMetadata || null,
  sourceErrors: Object.fromEntries(
    Object.keys(context?.loaderErrors || {}).map((key) => [key, context.loaderErrors[key]]),
  ),
  sourceMetadata: context?.sourceMetadata || null,
});

export const useBillingReconciliationRowEvidence = (params = {}, options = {}) =>
  useQuery({
    queryKey: rowEvidenceKey(params),
    queryFn: async ({ signal }) => {
      const [gmRows, cpqRows] = await Promise.all([
        listBillingReconciliationGmRows({ ...params, signal }),
        listBillingReconciliationCpqRows({ ...params, signal }),
      ]);
      return {
        gmRows: listRows(gmRows),
        cpqRows: listRows(cpqRows),
        appsheetData: null,
        dpeData: null,
      };
    },
    enabled: (options.enabled ?? true) && Boolean(params.runId && params.bac),
    staleTime: 30 * 1000,
    ...noRetry,
    ...options,
  });

export const useBillingReconciliationGmRows = (params = {}, options = {}) =>
  useQuery({
    queryKey: gmRowsKey(params),
    queryFn: ({ signal }) => listBillingReconciliationGmRows({ ...params, signal }),
    enabled: (options.enabled ?? true) && Boolean(params.runId),
    select: listRows,
    staleTime: 30 * 1000,
    ...noRetry,
    ...options,
  });

export const useBillingReconciliationAppSheetAccounts = (params = {}, options = {}) =>
  useQuery({
    queryKey: appSheetAccountSearchKey(params),
    queryFn: ({ signal }) => searchBillingReconciliationAppSheetAccounts({ ...params, signal }),
    enabled: (options.enabled ?? true) && Boolean(params.runId && String(params.search || '').trim().length >= 2),
    select: listRows,
    staleTime: 30 * 1000,
    ...noRetry,
    ...options,
  });

export const useBillingReconciliationBacAccounts = (params = {}, options = {}) =>
  useQuery({
    queryKey: appSheetBacAccountsKey(params),
    queryFn: ({ signal }) => listBillingReconciliationAppSheetBacAccounts({ limit: 100, ...params, signal }),
    enabled: (options.enabled ?? true) && Boolean(params.runId && params.bac),
    select: (payload) => payload?.accounts || [],
    staleTime: 30 * 1000,
    ...noRetry,
    ...options,
  });

export const useBillingReconciliationFullEvidence = (params = {}, options = {}) => {
  const isHistoricalBackfill = params.importMode === 'historical_backfill' || params.import_mode === 'historical_backfill';
  return useQuery({
    queryKey: isHistoricalBackfill ? [...rowEvidenceKey(params), 'full'] : operatorContextKey(params),
    queryFn: async ({ signal }) => {
      if (!isHistoricalBackfill) {
        const bundle = await getBillingReconciliationOperatorContext({ ...params, signal });
        const evidence = evidenceFromBacContext(bundle?.bacContext?.data);
        return {
          ...evidence,
          operatorBundle: bundle,
          workflow: {
            ...(evidence.workflow || {}),
            actions: bundle?.varianceActions?.data || evidence.workflow?.actions || [],
          },
        };
      }
      const [gmRows, cpqRows] = await Promise.all([
        listBillingReconciliationGmRows({ ...params, signal }),
        listBillingReconciliationCpqRows({ ...params, includeExternalContext: !isHistoricalBackfill, signal }),
      ]);
      if (isHistoricalBackfill) {
        const historicalFinalGmRows = await listBillingReconciliationHistoricalFinalGmRows({ ...params, signal });
        return {
          gmRows: listRows(gmRows),
          cpqRows: listRows(cpqRows),
          historicalFinalGmRows: listRows(historicalFinalGmRows),
          appsheetData: null,
          dpeData: null,
          sourceErrors: {
            gmRows: null,
            cpqRows: null,
            historicalFinalGmRows: null,
            appsheetData: null,
            dpeData: null,
          },
        };
      }
      const [historicalFinalGmRows, appsheetData, dpeData] = await Promise.allSettled([
        listBillingReconciliationHistoricalFinalGmRows({ ...params, signal }),
        listBillingReconciliationAppSheetRows({ ...params, signal }),
        listBillingReconciliationDpeRows({ ...params, signal }),
      ]);
      return {
        gmRows: listRows(gmRows),
        cpqRows: listRows(cpqRows),
        historicalFinalGmRows: historicalFinalGmRows.status === 'fulfilled' ? listRows(historicalFinalGmRows.value) : [],
        appsheetData: appsheetData.status === 'fulfilled' ? appsheetData.value : null,
        dpeData: dpeData.status === 'fulfilled' ? dpeData.value : null,
        sourceErrors: {
          gmRows: null,
          cpqRows: null,
          historicalFinalGmRows: historicalFinalGmRows.status === 'rejected' ? historicalFinalGmRows.reason : null,
          appsheetData: appsheetData.status === 'rejected' ? appsheetData.reason : null,
          dpeData: dpeData.status === 'rejected' ? dpeData.reason : null,
        },
      };
    },
    enabled: (options.enabled ?? true) && Boolean(params.runId && params.bac),
    staleTime: 30 * 1000,
    ...noRetry,
    ...options,
  });
};

export const useBillingReconciliationContextWarmStatus = (params = {}, options = {}) => {
  const queryClient = useQueryClient();
  const lastListInvalidation = useRef(null);
  const lastBacContextFingerprints = useRef(new Map());
  const query = useQuery({
    queryKey: contextWarmStatusKey(params),
    queryFn: ({ signal }) => getBillingReconciliationContextWarmStatus({ ...params, includeBacs: true, signal }),
    enabled: (options.enabled ?? true) && Boolean(params.runId),
    staleTime: 15 * 1000,
    refetchInterval: (query) => contextWarmStatusRefetchIntervalMs(query.state.data),
    ...noRetry,
    ...options,
  });
  useEffect(() => {
    if (!params.runId || !query.data) return;
    const warmed = Number(query.data.warmed || 0);
    const fingerprint = [
      warmed,
      Number(query.data.processing || 0),
      Number(query.data.queued || 0),
      Number(query.data.failed || 0),
      Number(query.data.unavailable || 0),
      Boolean(query.data.ready),
      query.data.latestWarmedAt || '',
    ].join(':');
    if (lastListInvalidation.current === fingerprint) return;
    lastListInvalidation.current = fingerprint;
    patchVarianceWarmStatuses(queryClient, { runId: params.runId, status: query.data });
    invalidateChangedTerminalBacContexts(queryClient, {
      runId: params.runId,
      rows: query.data.bacs,
      previousFingerprints: lastBacContextFingerprints.current,
    });
    if (query.data.ready && !query.data.bacs?.length) invalidateBillingVarianceLists(queryClient, { runId: params.runId });
  }, [params.runId, query.data, queryClient]);
  return query;
};

export const useQueueBillingReconciliationContextWarm = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: queueBillingReconciliationContextWarm,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: contextWarmStatusKey({ runId: variables?.runId, contextMode: variables?.contextMode || 'critical' }) });
    },
  });
};

export const useRefreshBillingReconciliationBacContext = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: refreshBillingReconciliationBacContext,
    onSuccess: (result, variables) => {
      if (result?.evidence || result?.workflow) {
        queryClient.setQueryData(bacContextKey({
          runId: variables?.runId,
          bac: variables?.bac,
          varianceId: variables?.varianceId || null,
          importMode: variables?.importMode || variables?.import_mode || null,
          contextMode: variables?.contextMode || 'critical',
        }), evidenceFromBacContext(result));
      }
      queryClient.invalidateQueries({ queryKey: contextWarmStatusKey({ runId: variables?.runId, contextMode: variables?.contextMode || 'critical' }) });
    },
  });
};
